import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LayoutGrid, SearchX } from "lucide-react";
import { Badge, FeedbackState, type DataTableColumn } from "@h2j-data-team/ui";
import type { CapacityRowsSort, CapacityUsageRow } from "@repo/shared";

import {
  ListError,
  ListLoading,
  QvdListToolbar,
  SpaceLink,
} from "@/components/qvd-analysis/qvd-list-parts";
import { SERVER_SORT_DIRECTION, useDebouncedValue } from "@/components/qvd-analysis/qvd-list-utils";
import { VirtualRowList } from "@/components/shared/VirtualRowList";
import { formatBytes } from "@/lib/health-format";
import {
  capacitySpacesQueryOptions,
  capacityUsageRowsInfiniteQueryOptions,
} from "@/features/capacity/api";

/**
 * Per-app consumption ranking of the measured day — both app origins
 * (`app_reload` + `app_import`), which is what `kind: "app"` selects server-side.
 * Search, space filter and sort run server-side too: the ranking is paged by
 * cursor, so a client-side filter would only ever see the rows already fetched.
 * Rows are virtualized and pulled a page at a time on scroll, the same grammar
 * the storage analysis' file list uses.
 */
export function CapacityByAppTable({
  snapshotId,
  dayTotalBytes,
}: {
  snapshotId: string;
  dayTotalBytes: number;
}) {
  const { t } = useTranslation("capacity");
  const [search, setSearch] = useState("");
  const [spaceFilter, setSpaceFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<CapacityRowsSort>("size");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: spaces } = useQuery(capacitySpacesQueryOptions(snapshotId, "app"));

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    capacityUsageRowsInfiniteQueryOptions(snapshotId, "app", {
      search: debouncedSearch,
      space: spaceFilter[0] ?? "",
      sort: sortKey,
    }),
  );

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const filtering = !!debouncedSearch || spaceFilter.length > 0;

  // The day's own total is the honest denominator; without the daily series
  // (older snapshot) fall back to what has been loaded.
  const total = dayTotalBytes > 0 ? dayTotalBytes : rows.reduce((s, r) => s + r.sizeBytes, 0);

  const columns = useMemo<DataTableColumn<CapacityUsageRow>[]>(
    () => [
      {
        id: "name",
        header: t("byApp.cols.app"),
        sort: "text",
        cell: (row) =>
          row.appName ? (
            <span className="block truncate font-semibold" title={row.appName}>
              {row.appName}
            </span>
          ) : (
            // Name never resolved (deleted app, or one the identity can't see):
            // the id IS the identification, not a footnote.
            <span className="block truncate font-mono">{row.appId ?? "—"}</span>
          ),
      },
      {
        id: "space",
        header: t("byApp.cols.space"),
        width: 180,
        cell: (row) => (
          <span className="text-dim">
            <SpaceLink spaceName={row.spaceName} />
          </span>
        ),
      },
      {
        id: "source",
        header: t("byApp.cols.source"),
        width: 150,
        cell: (row) => (
          <Badge variant="secondary" className="py-0">
            {t(`source.${row.source}`)}
          </Badge>
        ),
      },
      {
        id: "measurement",
        header: t("byApp.cols.measurement"),
        width: 160,
        cell: (row) =>
          row.measurement ? (
            <Badge variant={row.measurement === "daily" ? "info" : "outline"} className="py-0">
              {t(`measurement.${row.measurement}`)}
            </Badge>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        id: "size",
        header: t("byApp.cols.bytes"),
        width: 120,
        align: "end",
        variant: "numeric",
        sort: "number",
        cell: (row) => formatBytes(row.sizeBytes),
      },
      {
        id: "pct",
        header: t("byApp.cols.pct"),
        width: 100,
        align: "end",
        variant: "numeric",
        cell: (row) => (
          <span className="text-dim">
            {total > 0 ? `${((row.sizeBytes / total) * 100).toFixed(1)}%` : "—"}
          </span>
        ),
      },
    ],
    [t, total],
  );

  return (
    <div className="flex flex-col">
      {/* Outside the loading branch: a filter change starts a fresh query, and
          unmounting the toolbar under it would drop the focused search box. */}
      <QvdListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t("byApp.searchPlaceholder")}
        spaceOptions={(spaces?.data ?? []).map((s) => s.spaceName)}
        spaceFilter={spaceFilter}
        onSpaceFilter={setSpaceFilter}
        singleSpace
      />

      {isLoading ? (
        <ListLoading />
      ) : isError ? (
        <ListError error={error} title={t("error.load")} onRetry={() => void refetch()} />
      ) : rows.length === 0 ? (
        <FeedbackState
          state={filtering ? "no-results" : "empty"}
          icon={filtering ? <SearchX /> : <LayoutGrid />}
          title={filtering ? t("byApp.emptyFiltered") : t("byApp.empty")}
        />
      ) : (
        <VirtualRowList
          label={t("tabs.byApp")}
          columns={columns}
          rows={rows}
          getKey={(row) => row.id}
          sort={{ id: sortKey, direction: SERVER_SORT_DIRECTION[sortKey] }}
          onSort={(column) =>
            setSortKey(column.id === sortKey ? "size" : (column.id as CapacityRowsSort))
          }
          onReachEnd={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          loadingMoreLabel={isFetchingNextPage ? t("loadingMore") : undefined}
        />
      )}
    </div>
  );
}
